import { optionsColumn } from '@/global/components/DataGrid/hooks/useDataGridPro'
import ContentGuide from '@/global/guides/ContentGuide'
import { formatarHoras, hoursToMinutes as min } from '@acme/common'
import { Box, Divider, Skeleton, Typography } from '@mui/material'
import IconeAjuda from 'global/components/IconeAjuda'
import SaldoBox from 'global/components/SaldoBox'
import { AlertTriangleIcon, TriangleAlertIcon } from 'lucide-react'
import { usePagamentoHorasOutletContext } from 'pages/PagamentoHoras'
import { useEffect, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import {
    funcionario as funcionario_api,
    regrasCalculo,
    SaldoHoras,
} from 'service/api'

interface IProps {
    limite: Partial<SaldoHoras>
}

interface TipoHora {
    tipo: string
    quantidade: string
}

export default function FormPagamento({ limite }: IProps) {
    const { setValue, watch } = useFormContext()
    const { data: regras } = regrasCalculo.useFetch()
    const { funcionarioId = '-1' } = useParams()
    const { data } = usePagamentoHorasOutletContext()
    const { data: funcionario } = funcionario_api.useFetchById(
        Number(funcionarioId),
    )
    const saldo = data?.find(
        item => item.saldo.funcionarioId === Number(funcionarioId),
    )?.saldo
    const [porcentagens, faltas] = watch(['porcentagens', 'faltas'])

    const [tipos, setTipos] = useState<TipoHora[]>()

    useEffect(() => {
        if (!regras || !funcionario) return
        else {
            const opcoes =
                regras
                    ?.find(r => r.id == funcionario?.regraCalculo)
                    ?.porcentagens.map(p => p.porcentagem.toString()) ?? []

            const defaultValues = Object.entries(porcentagens ?? {})
                .map(([key, value]) => ({
                    tipo: key,
                    quantidade: formatarHoras(value as any, 3),
                }))
                .concat({
                    tipo: 'Faltas',
                    quantidade: formatarHoras(faltas, 3),
                })

            setTipos(
                [
                    ...opcoes
                        .map(v => ({
                            tipo: v,
                            quantidade: '000:00',
                        }))
                        .filter(
                            v => !defaultValues.some(dv => dv.tipo === v.tipo),
                        ),
                    ...defaultValues,
                ].sort((a, b) => parseInt(a.tipo) - parseInt(b.tipo)),
            )
        }
    }, [regras, funcionario?.id])

    useEffect(() => {
        setValue(
            'valores',
            tipos?.reduce((acc, curr) => {
                return {
                    ...acc,
                    [curr.tipo]: curr.quantidade,
                }
            }, {}),
        )
    }, [tipos])

    if (!limite || !funcionario) return <Loading />

    return (
        <>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    flexGrow: 1,
                    gap: 2,
                    p: 2,
                    mb: 2,
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: 1,
                    alignItems: 'center',
                    justifyContent: 'space-evenly',
                }}
            >
                <Typography
                    variant="h6"
                    sx={{
                        maxWidth: '65%',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    }}
                    title={funcionario?.nome}
                >
                    {funcionario?.nome}
                </Typography>
                {saldo && saldo.id ? (
                    <SaldoBox
                        title="Saldo atual"
                        extras={saldo?.horasExtras}
                        faltas={saldo?.faltas}
                        porcentagens={
                            (saldo?.porcentagemHorasExtras as any) ?? {}
                        }
                        sx={{ border: 'none' }}
                    />
                ) : (
                    <div className="flex flex-row items-center gap-2">
                        <span className="text-sm font-semibold">
                            Saldo atual
                        </span>
                        <Divider orientation="vertical" flexItem />
                        <div className="text-error font-bold inline-flex items-center gap-1">
                            <AlertTriangleIcon className="size-4" />
                            Não calculado
                        </div>
                    </div>
                )}
            </Box>
            <ContentGuide.DataGrid
                loading={false}
                ToolbarProps={{ hideToolbar: true }}
                hideFooter
                columns={[
                    {
                        ...optionsColumn,
                        field: 'tipo',
                        headerName: '',
                        width: 200,
                        valueFormatter: ({ value }) =>
                            !isNaN(Number(value))
                                ? `Horas extras ${value}%`
                                : value,
                    },
                    {
                        ...optionsColumn,
                        field: 'limite',
                        headerName: 'Limite',
                        width: 150,
                        headerAlign: 'center',
                        align: 'left',
                        valueGetter: ({ value }) => formatarHoras(value, 3),
                        renderCell: ({ value, row }) => (
                            <Typography
                                variant="body2"
                                sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    gap: 3,
                                }}
                            >
                                {value}{' '}
                                {min(row.quantidade) > min(value) && (
                                    <IconeAjuda
                                        size="small"
                                        Icon={
                                            <TriangleAlertIcon className="text-yellow-500" />
                                        }
                                    >
                                        O pagamento não pode superar o limite
                                    </IconeAjuda>
                                )}
                            </Typography>
                        ),
                        renderHeader: () => (
                            <Typography
                                variant="body2"
                                sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    gap: 3,
                                }}
                                onClick={e => e.stopPropagation()}
                            >
                                Limite{' '}
                                <IconeAjuda>
                                    (Apenas leitura) Representa a soma do saldo
                                    atual com o valor do pagamento, o pagamento
                                    não pode superar o limite.
                                </IconeAjuda>
                            </Typography>
                        ),
                    },
                    {
                        ...optionsColumn,
                        field: 'quantidade',
                        headerName: 'Pagamento',
                        flex: 1,
                        editable: true,
                        headerAlign: 'center',
                        renderHeader: () => (
                            <Typography
                                variant="body2"
                                sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    gap: 3,
                                }}
                                onClick={e => e.stopPropagation()}
                            >
                                Pagamento{' '}
                                <IconeAjuda size="small">
                                    (Editável) Clique sobre os campos para
                                    editar.
                                </IconeAjuda>
                            </Typography>
                        ),
                    },
                ]}
                rows={
                    tipos?.map((v, i) => ({
                        ...v,
                        limite:
                            v.tipo === 'Faltas'
                                ? limite?.faltas
                                : (limite?.porcentagemHorasExtras as any)?.[
                                      v.tipo
                                  ],
                        id: i,
                    })) || []
                }
                headerHeight={36}
                rowHeight={35}
                onCellEditCommit={({ id, field, value: rawValue = '' }) => {
                    let value = String(rawValue).trim()
                    if (field !== 'quantidade') return
                    if (!/^\d{1,3}:\d{2}$/.test(value)) {
                        if (value.includes(':')) {
                            const [hr, min] = value.split(':')
                            value = `${hr.padStart(3, '0')}:${min.padEnd(
                                2,
                                '0',
                            )}`
                        } else value = `${value.padStart(3, '0')}:00`
                    }

                    const clone =
                        tipos?.map((v, i) =>
                            i === id ? { ...v, [field]: value } : v,
                        ) ?? []

                    const porcentagensClone = clone.filter(
                        it => !isNaN(Number(it.tipo)),
                    )

                    const horasTotais = porcentagensClone.reduce(
                        (acc, curr) => acc + min(curr.quantidade),
                        0,
                    )

                    const porcentagens = porcentagensClone.reduce(
                        (acc, curr) =>
                            isNaN(Number(curr.tipo))
                                ? acc
                                : {
                                      ...acc,
                                      [curr.tipo]: formatarHoras(
                                          curr.quantidade,
                                          3,
                                      ),
                                  },
                        {},
                    )

                    setValue('porcentagens', porcentagens)
                    setValue('horas', formatarHoras(horasTotais, 3))
                    setValue(
                        'faltas',
                        clone.find(v => v.tipo === 'Faltas')?.quantidade,
                    )
                    setTipos(clone)
                }}
                style={{ minHeight: 140 }}
            />
        </>
    )
}

function Loading() {
    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                flexGrow: 1,
                gap: 2,
            }}
        >
            <Skeleton variant="rectangular" width="100%" height={80} />
            <Skeleton variant="rectangular" width="100%" height={150} />
        </Box>
    )
}
